import { defineMessage, type MessageDefinition, type MessagePayload } from "../messaging/messenger"
import { normalizeDataDependencies, type DataDependency, type DataDependencyInput } from "../data/data-invalidation"
import type { RemoteFile } from "./remote-file"

declare const remoteCommandResult: unique symbol

type RemoteCommandInvalidationResolver<TPayload extends object> = {
  bivarianceHack(event: Readonly<TPayload>): readonly DataDependency[]
}["bivarianceHack"]

export interface RemoteCommandEventDefinition<TPayload extends object> extends MessageDefinition<Readonly<TPayload>> {
  readonly invalidates?: RemoteCommandInvalidationResolver<TPayload>
}

export type AnyRemoteCommandEventDefinition = RemoteCommandEventDefinition<object>
export type RemoteCommandEventDefinitions = Readonly<Record<string, AnyRemoteCommandEventDefinition>>

export type RemoteCommandEventPayload<TDefinition extends AnyRemoteCommandEventDefinition> = MessagePayload<TDefinition>

export interface RemoteCommandEvent {
  readonly $type: string
  readonly [key: string]: unknown
}

export interface RemoteCommandResult<TResult> {
  readonly isSucceeded: boolean
  readonly value: TResult | null
  readonly events?: readonly RemoteCommandEvent[] | null
}

export function getRemoteCommandDisplayMessage(
  result: Pick<RemoteCommandResult<unknown>, "events">
): string | undefined {
  const message = result.events?.find((event) => typeof event.displayMessage === "string")?.displayMessage
  return typeof message === "string" ? message : undefined
}

type RemoteCommandEventValue<TDefinition extends AnyRemoteCommandEventDefinition> = Readonly<
  RemoteCommandEventPayload<TDefinition>
>

/** The events a command declares, read from a command result. */
export type RemoteCommandEvents<TEvents extends RemoteCommandEventDefinitions | undefined> =
  TEvents extends RemoteCommandEventDefinitions
    ? {
        /** Every event of each declared type, in the order the server published them. */
        readonly all: { readonly [TName in keyof TEvents]: readonly RemoteCommandEventValue<TEvents[TName]>[] }
      } & {
        /** The one event of this type, or undefined when the server published none. Throws when it published several. */
        readonly [TName in keyof TEvents]: RemoteCommandEventValue<TEvents[TName]> | undefined
      }
    : { readonly all: Readonly<Record<never, never>> }

/** What a succeeded command returns to its caller: the value and the typed events. */
export type RemoteCommandOutcome<TValue, TEvents extends RemoteCommandEventDefinitions | undefined> = {
  readonly value: TValue | null
} & RemoteCommandEvents<TEvents>

/** Thrown when a command published several events of one type and the caller reads the single-event property. */
export class AmbiguousRemoteCommandEventError extends Error {
  constructor(
    readonly route: string,
    readonly eventKey: string,
    readonly count: number
  ) {
    super(
      `Remote command "${route}" published ${count} "${eventKey}" events, so "${eventKey}" is ambiguous. Read all.${eventKey} instead.`
    )
    this.name = "AmbiguousRemoteCommandEventError"
  }
}

/**
 * Thrown by `CommandClient.run` when the server answers a command with `isSucceeded: false`.
 * The result carries the failure's violations as events; `eventsOf` reads them typed for one command,
 * so an action that runs several commands can tell which one failed.
 */
export class RemoteCommandFailure extends Error {
  readonly displayMessage: string | undefined

  constructor(
    readonly route: string,
    readonly result: Readonly<RemoteCommandResult<unknown>>
  ) {
    const displayMessage = getRemoteCommandDisplayMessage(result)
    super(displayMessage ?? `Remote command "${route}" failed.`)
    this.name = "RemoteCommandFailure"
    this.displayMessage = displayMessage
  }

  /**
   * The declared events of `command` in this failure, or undefined when another command failed.
   * `failure.eventsOf(linkBoxCommand)?.OrderMismatch`
   */
  eventsOf<TEvents extends RemoteCommandEventDefinitions | undefined>(command: {
    readonly route: string
    readonly events: TEvents
  }): RemoteCommandEvents<TEvents> | undefined {
    return command.route === this.route ? readRemoteCommandEvents(command, this.result) : undefined
  }
}

/**
 * The field a failure event belongs to: a field name, or a function of the event when the field depends on its
 * payload, such as `lines.3.quantity` for the order line the event names. A function that returns undefined leaves
 * the message on the form.
 */
export type CommandFailureField<TPayload, TField extends string> =
  TField | ((event: TPayload) => TField | null | undefined)

/** Which declared failure event of a command belongs to which field. */
export type CommandFailureFields<
  TEvents extends RemoteCommandEventDefinitions | undefined,
  TField extends string,
> = TEvents extends RemoteCommandEventDefinitions
  ? {
      readonly [TName in keyof TEvents & string]?: CommandFailureField<RemoteCommandEventValue<TEvents[TName]>, TField>
    }
  : Readonly<Record<never, never>>

export interface CommandFailureMessages<TField extends string> {
  /** Messages that belong to no field, in the order the server published them. Empty when the failure has none. */
  readonly form: readonly string[]
  readonly fields: Readonly<Partial<Record<TField, readonly string[]>>>
}

/**
 * Splits the messages of a failed command over fields and the form. Generated failure events carry only a
 * `displayMessage`, so `fields` names which declared event belongs to which field. Every other event with a
 * `displayMessage` becomes a form message. A failure of another command keeps only its display message.
 */
export function mapCommandFailure<TEvents extends RemoteCommandEventDefinitions | undefined, TField extends string>(
  failure: RemoteCommandFailure,
  command: { readonly route: string; readonly events: TEvents },
  fields?: CommandFailureFields<TEvents, TField>
): CommandFailureMessages<TField> {
  if (failure.route !== command.route) {
    const noFields: Partial<Record<TField, readonly string[]>> = {}
    return { form: failure.displayMessage == null ? [] : [failure.displayMessage], fields: noFields }
  }

  const fieldsByType = new Map<string, CommandFailureField<RemoteCommandEvent, TField>>()
  const definitions: RemoteCommandEventDefinitions = command.events ?? {}
  for (const [name, field] of Object.entries(fields ?? {}) as [
    string,
    CommandFailureField<RemoteCommandEvent, TField> | undefined,
  ][]) {
    const definition = definitions[name]
    if (definition != null && field != null) fieldsByType.set(definition.type, field)
  }

  const form: string[] = []
  const fieldMessages: Partial<Record<TField, string[]>> = {}
  for (const event of failure.result.events ?? []) {
    if (event == null || typeof event.displayMessage !== "string") continue
    const target = fieldsByType.get(event.$type)
    const field = typeof target === "function" ? target(event) : target
    if (field == null) form.push(event.displayMessage)
    else (fieldMessages[field] ??= []).push(event.displayMessage)
  }
  return { form, fields: fieldMessages }
}

export interface RemoteCommand<TInput, TResult, TEvents extends RemoteCommandEventDefinitions | undefined = undefined> {
  readonly route: string
  readonly input: TInput
  readonly events: TEvents
  /** "file" when the server answers with the raw file: the command then resolves to a RemoteFile. */
  readonly result?: "file"
  readonly [remoteCommandResult]: TResult
}

export interface RemoteCommandDefinition<
  TInput,
  TResult,
  TEvents extends RemoteCommandEventDefinitions | undefined = undefined,
> {
  (input: TInput): RemoteCommand<TInput, TResult, TEvents>
  readonly route: string
  readonly events: TEvents
  readonly result?: "file"
  withEvents<TNextEvents extends RemoteCommandEventDefinitions>(
    events: TNextEvents
  ): RemoteCommandDefinition<TInput, TResult, TNextEvents>
}

/** The options of a command whose result is a file, which the server writes as the raw file instead of JSON. */
export interface RemoteCommandFileOptions {
  readonly result: "file"
}

export function defineRemoteCommand<TInput, TResult>(route: string): RemoteCommandDefinition<TInput, TResult, undefined>
export function defineRemoteCommand<TInput, TResult extends RemoteFile>(
  route: string,
  options: RemoteCommandFileOptions
): RemoteCommandDefinition<TInput, TResult, undefined>
export function defineRemoteCommand<TInput, TResult>(route: string, options?: RemoteCommandFileOptions) {
  return createRemoteCommandDefinition<TInput, TResult, undefined>(route, undefined, options?.result)
}

export interface RemoteCommandEventOptions<TPayload extends object> {
  readonly invalidates?: DataDependencyInput | ((event: Readonly<TPayload>) => DataDependencyInput)
}

export function defineRemoteCommandEvent<TPayload extends object>(
  type: string,
  options: RemoteCommandEventOptions<TPayload> = {}
) {
  const message = defineMessage<Readonly<TPayload>>(type)
  const configuredInvalidates = options.invalidates
  const invalidates =
    configuredInvalidates == null
      ? undefined
      : typeof configuredInvalidates === "function"
        ? (event: Readonly<TPayload>) => normalizeDataDependencies(configuredInvalidates(event))
        : () => normalizeDataDependencies(configuredInvalidates)
  return Object.freeze({ ...message, invalidates }) as RemoteCommandEventDefinition<TPayload>
}

/**
 * Reads the declared events of a command from its result. The result of a failed command carries
 * its violations as events, so this works for failures too. Events the command does not declare are ignored.
 */
export function readRemoteCommandEvents<TEvents extends RemoteCommandEventDefinitions | undefined>(
  command: { readonly route: string; readonly events: TEvents },
  result: Pick<RemoteCommandResult<unknown>, "events">
): RemoteCommandEvents<TEvents> {
  const definitions: RemoteCommandEventDefinitions = command.events ?? {}
  const raised = result.events ?? []
  const all: Record<string, readonly object[]> = {}
  const events: Record<string, unknown> = {}
  for (const [name, definition] of Object.entries(definitions)) {
    const matching = Object.freeze(raised.filter((event) => event != null && event.$type === definition.type))
    all[name] = matching
    if (matching.length > 1) {
      Object.defineProperty(events, name, {
        enumerable: false,
        get() {
          throw new AmbiguousRemoteCommandEventError(command.route, name, matching.length)
        },
      })
    } else {
      events[name] = matching[0]
    }
  }
  events.all = Object.freeze(all)
  return Object.freeze(events) as RemoteCommandEvents<TEvents>
}

/**
 * Builds the outcome of a succeeded command result: the value and the typed events.
 * The descriptors keep the getters of events that were published more than once.
 */
export function createRemoteCommandOutcome<TInput, TValue, TEvents extends RemoteCommandEventDefinitions | undefined>(
  command: RemoteCommand<TInput, RemoteCommandResult<TValue>, TEvents>,
  result: RemoteCommandResult<TValue>
): RemoteCommandOutcome<TValue, TEvents> {
  const outcome = Object.defineProperties(
    {},
    {
      ...Object.getOwnPropertyDescriptors(readRemoteCommandEvents(command, result)),
      value: { value: result.value, enumerable: true },
    }
  )
  return Object.freeze(outcome) as RemoteCommandOutcome<TValue, TEvents>
}

const reservedEventKeys: readonly string[] = ["value", "all"]

function createRemoteCommandDefinition<TInput, TResult, TEvents extends RemoteCommandEventDefinitions | undefined>(
  route: string,
  events: TEvents,
  result?: "file"
): RemoteCommandDefinition<TInput, TResult, TEvents> {
  const flag = result === undefined ? {} : { result }
  const definition = ((input: TInput) => ({
    route,
    input,
    events,
    ...flag,
  })) as RemoteCommandDefinition<TInput, TResult, TEvents>

  return Object.assign(definition, {
    route,
    events,
    ...flag,
    withEvents<TNextEvents extends RemoteCommandEventDefinitions>(nextEvents: TNextEvents) {
      for (const key of reservedEventKeys) {
        if (Object.prototype.hasOwnProperty.call(nextEvents, key)) {
          throw new Error(`Remote command "${route}" cannot declare an event named "${key}": the name is reserved.`)
        }
      }
      return createRemoteCommandDefinition<TInput, TResult, TNextEvents>(route, nextEvents, result)
    },
  })
}
